import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
} from '@mui/material';
import Header from './Header';
import ConfirmDialog from './ConfirmDialog';
import api from '../api/api';

function ManageSmartDevicesPage() {
    const { t } = useTranslation();

    const [smartDevices, setSmartDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [openDialog, setOpenDialog] = useState(false);
    const [currentDevice, setCurrentDevice] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deviceToDelete, setDeviceToDelete] = useState(null);

    useEffect(() => {
        fetchSmartDevices();
    }, []);

    const fetchSmartDevices = async () => {
        try {
            setLoading(true);
            const response = await api.get('/smart-device');
            setSmartDevices(response.data);
            setError('');
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
            setSmartDevices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddDevice = () => {
        setIsEditMode(false);
        setCurrentDevice({ code: '', description: '', url: 'http://', model: '', type: '', actions: [] });
        setOpenDialog(true);
    };

    const handleEditDevice = (device) => {
        setIsEditMode(true);
        setCurrentDevice({ ...device, actions: device.actions.join(', ') });
        setOpenDialog(true);
    };

    const handleDeleteDevice = (deviceCode) => {
        setDeviceToDelete(deviceCode);
        setDeleteConfirmOpen(true);
    };

    const confirmDeleteDevice = async () => {
        if (!deviceToDelete) return;
        try {
            await api.delete(`/smart-device/${deviceToDelete}`);
            setMessage(t('common.deviceDeletedSuccess'));
            fetchSmartDevices();
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
        }
        setDeviceToDelete(null);
    };

    const handleSaveDevice = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        // Basic URL validation
        if (!currentDevice.url.startsWith('http://') && !currentDevice.url.startsWith('https://')) {
            setError(t('common.urlValidationError'));
            return;
        }

        const deviceToSave = {
            ...currentDevice,
            actions: currentDevice.actions.split(',').map(s => s.trim()).filter(s => s !== ''),
        };

        try {
            if (isEditMode) {
                await api.put(`/smart-device/${deviceToSave.code}`, deviceToSave);
                setMessage(t('common.deviceUpdatedSuccess'));
            } else {
                await api.post('/smart-device', deviceToSave);
                setMessage(t('common.deviceAddedSuccess'));
            }
            setOpenDialog(false);
            fetchSmartDevices();
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentDevice(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentDevice((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Header title={t('common.manageSmartDevices')} />

            <Container sx={{ mt: 4 }}>
                {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Button variant="contained" sx={{ mb: 2 }} onClick={handleAddDevice}>
                    {t('common.add')} {t('common.manageSmartDevices')}
                </Button>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="smart device table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('common.deviceCode')}</TableCell>
                                    <TableCell>{t('common.description')}</TableCell>
                                    <TableCell>{t('common.type')}</TableCell>
                                    <TableCell>{t('common.model')}</TableCell>
                                    <TableCell>{t('common.url')}</TableCell>
                                    <TableCell>{t('common.actions')}</TableCell>
                                    <TableCell align="right">{t('common.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {smartDevices.map((device) => (
                                    <TableRow
                                        key={device.code}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {device.code}
                                        </TableCell>
                                        <TableCell>{device.description}</TableCell>
                                        <TableCell>{device.type}</TableCell>
                                        <TableCell>{device.model}</TableCell>
                                        <TableCell>{device.url}</TableCell>
                                        <TableCell>{device.actions?.join(', ')}</TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => handleEditDevice(device)}>
                                                {t('common.edit')}
                                            </Button>
                                            <Button size="small" onClick={() => handleDeleteDevice(device.code)} color="error">
                                                {t('common.delete')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Container>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>{isEditMode ? t('common.edit') : t('common.add')} {t('common.manageSmartDevices')}</DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSaveDevice} sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="code"
                            name="code"
                            label={t('common.deviceCode')}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={currentDevice?.code || ''}
                            onChange={handleChange}
                            disabled={isEditMode}
                            required
                        />
                        <TextField
                            margin="dense"
                            id="description"
                            name="description"
                            label={t('common.description')}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={currentDevice?.description || ''}
                            onChange={handleChange}
                            required
                        />

                        <FormControl fullWidth margin="dense" variant="standard" required>
                            <InputLabel id="type-select-label">{t('common.type')}</InputLabel>
                            <Select
                                labelId="type-select-label"
                                id="type"
                                name="type"
                                value={currentDevice?.type || ''}
                                label={t('common.type')}
                                onChange={handleChange}
                            >
                                <MenuItem value="AIRCON">AIRCON</MenuItem>
                                <MenuItem value="AUTOMATION">AUTOMATION</MenuItem>
                                <MenuItem value="GATE">GATE</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            margin="dense"
                            id="model"
                            name="model"
                            label={t('common.model')}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={currentDevice?.model || ''}
                            onChange={handleChange}
                        />

                        <TextField
                            margin="dense"
                            id="url"
                            name="url"
                            label={t('common.url')}
                            type="url"
                            fullWidth
                            variant="standard"
                            value={currentDevice?.url || ''}
                            onChange={(e) => {
                                let value = e.target.value;
                                if (!value.startsWith('http://') && !value.startsWith('https://')) {
                                    value = `http://${value}`;
                                }
                                setCurrentDevice((prev) => ({ ...prev, url: value }));
                            }}
                            required
                            helperText={t('common.urlHelperText')}
                        />

                        <TextField
                            margin="dense"
                            id="actions"
                            name="actions"
                            label={t('common.actions')}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={currentDevice?.actions || ''}
                            onChange={handleChange}
                            helperText={t('common.actionsHelperText')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
                    <Button onClick={handleSaveDevice} variant="contained">{t('common.save')}</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={confirmDeleteDevice}
                title={t('common.confirmDelete')}
                message={t('common.confirmDeleteDevice', { deviceCode: deviceToDelete })}
            />
        </Box>
    );
}

export default ManageSmartDevicesPage;